import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react'
import type { PermissionsHash } from '@/types/permissions'
import { refreshToken as refreshTokenApi } from '@/services/authApi'
import { disconnectConsumer } from '@/services/cable'
import { socialLogin as socialLoginApi } from '@/services/socialAuth'
import {
  bootstrapTokensSync,
  hydrateTokens,
  saveTokens,
  clearTokens,
} from '@/platform/auth'
import { apiUrl } from '@/platform/config'
import { activateSessionGuard, UNAUTHORIZED_EVENT } from '@/platform/sessionGuard'

// On native (Capacitor) the WebView origin is `https://localhost`, so a bare
// `/api/v1/...` path resolves against the WebView itself (404 → JSON parse
// throws → "Network error" toast). `apiBaseUrl()` resolves to
// `VITE_API_BASE_URL` on native and to '' on web (same-origin Rails).

export interface VerificationTierCard {
  tier: number
  key: 'account' | 'identity' | 'full'
  name: string
  requirement: string
  status: 'unstarted' | 'pending' | 'processing' | 'requires_input' | 'canceled' | 'verified'
  locked: boolean
  evidence: Record<string, boolean | string | null>
}

interface AuthUser {
  id: number
  email: string
  firstName: string
  lastName: string
  fullName: string
  role: string // "platform_member" | "platform_admin"
  platformMembershipStatus?: string // "active" | "suspended" | "banned"
  platformMembershipType?: string // "free" | "premium" | "founding"
  trustScore: number
  trustLevel: string
  trustScoreDetails?: {
    score: number
    trend: string
    factors: Record<string, { label: string; score: number; weight: number }>
    lastCalculated: string
    modelVersion: string
  } | null
  avatarUrl: string | null
  kycVerified: boolean
  kycProvider: 'stub' | 'stripe_identity'
  kycStatus: string | null
  identityVerified: boolean
  paymentVerified: boolean
  verified: boolean
  phoneVerified: boolean
  // The identity ladder: 0 Unverified · 1 Account · 2 Identity · 3 Full.
  // `verified` stays a separate fact — see User#verified? — because an account
  // verified before phone verification existed still sits at tier 0.
  verificationTier: number
  verificationTiers: VerificationTierCard[]
  phone?: string | null
  phoneCountryCode?: string | null
  bio?: string | null
  location?: string | null
  gender?: string | null
  street?: string | null
  houseNumber?: string | null
  postcode?: string | null
  city?: string | null
  addressCountryCode?: string | null
  dateOfBirth?: string | null
  nationality?: string | null
  fatherName?: string | null
  fatherPhone?: string | null
  motherName?: string | null
  motherPhone?: string | null
  emergencyContactName?: string | null
  emergencyContactRelationship?: string | null
  emergencyContactPhone?: string | null
  preferredLanguage?: string
  timezone?: string
  authProvider?: string
  emailVerified?: boolean
  unconfirmedEmail?: string | null
  createdAt?: string | null
  updatedAt?: string | null
  hasPaymentMethod?: boolean
  isMafaoEmployee?: boolean
  isCircleManager?: boolean
  /** Employee ID (e.g. "MAF-001") if the user is a Mafao employee. */
  employeeId?: string | null
  participation?: {
    activeCircles: number
    completedCircles: number
    referencesCount: number
    totalContributed: number
    onTimeRate: number
  }
  onboardingComplete?: boolean
  failedLoginAttempts?: number
  lockedUntil?: string | null
  associationRoles?: Array<{
    associationId: string
    role: string // "member" | "organizer" | "treasurer" | "secretary" | "president"
    associationName: string
  }>
}

interface AuthContextValue {
  user: AuthUser | null
  token: string | null
  permissions: PermissionsHash | null
  isAuthenticated: boolean
  /**
   * True while the session is still being restored on a fresh load — native
   * tokens hydrate asynchronously and the user is fetched via /auth/me. Guards
   * should show a loading state rather than treating this as "logged out",
   * otherwise a deep-link reload bounces to /login before restore finishes
   * (cm-gap-analysis E2).
   */
  isRestoring: boolean
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>
  /**
   * Adopts the session the registration wizard hands back.
   *
   * There is no `register` any more: an account is not created from a single
   * form, so nothing here can create one. The wizard proves both channels and
   * completes, and this takes the token it returns.
   */
  adoptSession: (payload: { token: string; refresh_token?: string; user: Record<string, unknown> }) => void
  forgotPassword: (email: string) => Promise<{ success: boolean; error?: string }>
  resetPassword: (token: string, password: string, passwordConfirmation: string) => Promise<{ success: boolean; error?: string }>
  resendVerification: () => Promise<{ success: boolean; error?: string }>
  updateOnboarding: (data: Record<string, unknown>) => Promise<{ success: boolean; trustBonus?: number; error?: string }>
  updateProfile: (data: Record<string, unknown> | FormData) => Promise<{ success: boolean; error?: string }>
  completeOnboarding: () => Promise<{ success: boolean; error?: string }>
  socialLogin: (provider: string, idToken: string, userInfo?: { firstName?: string; lastName?: string }) => Promise<{ success: boolean; error?: string }>
  refreshUser: () => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  token: null,
  permissions: null,
  isAuthenticated: false,
  isRestoring: true,
  login: async () => ({ success: false }),
  adoptSession: () => {},
  forgotPassword: async () => ({ success: false }),
  resetPassword: async () => ({ success: false }),
  resendVerification: async () => ({ success: false }),
  updateOnboarding: async () => ({ success: false }),
  updateProfile: async () => ({ success: false }),
  completeOnboarding: async () => ({ success: false }),
  socialLogin: async () => ({ success: false }),
  refreshUser: async () => {},
  logout: () => {},
})

function readMetaUser(): AuthUser | null {
  const meta = document.querySelector('meta[name="current-user"]')
  if (meta) {
    try {
      return JSON.parse(meta.getAttribute('content') || '')
    } catch {
      return null
    }
  }
  return null
}

function readMetaToken(): string | null {
  const meta = document.querySelector('meta[name="jwt-token"]')
  return meta?.getAttribute('content') || null
}

function mapUser(u: Record<string, unknown>): AuthUser {
  return {
    id: u.id as number,
    email: u.email as string,
    firstName: u.first_name as string,
    lastName: u.last_name as string,
    fullName: u.full_name as string,
    role: u.role as string,
    trustScore: u.trust_score as number,
    trustLevel: u.trust_level as string,
    trustScoreDetails: u.trust_score_details ? (() => {
      const d = u.trust_score_details as Record<string, unknown>
      return {
        score: d.score as number,
        trend: d.trend as string,
        factors: d.factors as Record<string, { label: string; score: number; weight: number }>,
        lastCalculated: d.last_calculated as string,
        modelVersion: d.model_version as string,
      }
    })() : null,
    avatarUrl: u.avatar_url as string | null,
    kycVerified: u.kyc_verified as boolean,
    kycProvider: (u.kyc_provider ?? 'stub') as 'stub' | 'stripe_identity',
    kycStatus: (u.kyc_status ?? null) as string | null,
    identityVerified: (u.identity_verified ?? u.kyc_verified) as boolean,
    paymentVerified: (u.payment_verified ?? false) as boolean,
    verified: (u.verified ?? false) as boolean,
    phoneVerified: (u.phone_verified ?? false) as boolean,
    verificationTier: (u.verification_tier ?? 0) as number,
    verificationTiers: (u.verification_tiers ?? []) as VerificationTierCard[],
    phone: u.phone as string | null,
    phoneCountryCode: u.phone_country_code as string | null,
    bio: u.bio as string | null,
    location: u.location as string | null,
    gender: u.gender as string | null,
    street: u.street as string | null,
    houseNumber: u.house_number as string | null,
    postcode: u.postcode as string | null,
    city: u.city as string | null,
    addressCountryCode: u.address_country_code as string | null,
    dateOfBirth: u.date_of_birth as string | null,
    nationality: u.nationality as string | null,
    fatherName: (u.father_name ?? null) as string | null,
    fatherPhone: (u.father_phone ?? null) as string | null,
    motherName: (u.mother_name ?? null) as string | null,
    motherPhone: (u.mother_phone ?? null) as string | null,
    emergencyContactName: (u.emergency_contact_name ?? null) as string | null,
    emergencyContactRelationship: (u.emergency_contact_relationship ?? null) as string | null,
    emergencyContactPhone: (u.emergency_contact_phone ?? null) as string | null,
    preferredLanguage: u.preferred_language as string,
    timezone: u.timezone as string,
    authProvider: u.auth_provider as string,
    emailVerified: u.email_verified as boolean,
    unconfirmedEmail: (u.unconfirmed_email as string) || null,
    createdAt: (u.created_at as string) || null,
    updatedAt: (u.updated_at as string) || null,
    hasPaymentMethod: u.has_payment_method as boolean,
    isMafaoEmployee: u.is_mafao_employee as boolean,
    isCircleManager: u.is_circle_manager as boolean,
    employeeId: (u.employee_id as string) || null,
    participation: u.participation ? (() => {
      const p = u.participation as Record<string, unknown>
      return {
        activeCircles: p.active_circles as number,
        completedCircles: p.completed_circles as number,
        referencesCount: p.references_count as number,
        totalContributed: p.total_contributed as number,
        onTimeRate: p.on_time_rate as number,
      }
    })() : undefined,
    onboardingComplete: u.onboarding_complete as boolean,
    failedLoginAttempts: u.failed_login_attempts as number,
    lockedUntil: u.locked_until as string | null,
    platformMembershipStatus: u.platform_membership_status as string,
    platformMembershipType: u.platform_membership_type as string,
    associationRoles: u.association_roles as AuthUser['associationRoles'],
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => {
    return readMetaUser()
  })

  const [token, setToken] = useState<string | null>(() => {
    return readMetaToken() || bootstrapTokensSync().token
  })

  const [refreshTokenValue, setRefreshTokenValue] = useState<string | null>(() => {
    return bootstrapTokensSync().refreshToken
  })

  // E2 — session-restore gate. True until we've determined the auth state on a
  // fresh load (native tokens hydrate async; the user is fetched via /auth/me).
  // Starts false only when a user is already known synchronously (web meta
  // hydration), so there's no spurious spinner in that common case.
  const [isRestoring, setIsRestoring] = useState<boolean>(() => !readMetaUser())
  // Whether the async token hydration (native Preferences) has completed.
  const [tokensHydrated, setTokensHydrated] = useState(false)

  // Native builds cannot read persisted tokens synchronously (Capacitor
  // Preferences is async-only). Hydrate from the async backing store once
  // on mount so the user stays signed in across app launches.
  useEffect(() => {
    let cancelled = false
    void hydrateTokens().then((hydrated) => {
      if (cancelled) return
      if (hydrated.token && !token) setToken(hydrated.token)
      if (hydrated.refreshToken && !refreshTokenValue) setRefreshTokenValue(hydrated.refreshToken)
      setTokensHydrated(true)
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Resolve the restore gate: done once we have a user, or once hydration has
  // settled and there's no token to restore from (genuinely logged out).
  useEffect(() => {
    if (user) {
      setIsRestoring(false)
    } else if (tokensHydrated && !token) {
      setIsRestoring(false)
    }
  }, [user, token, tokensHydrated])

  const [permissions, setPermissions] = useState<PermissionsHash | null>(null)
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Persist tokens through the platform storage layer (localStorage on web,
  // Capacitor Preferences on native).
  useEffect(() => {
    void saveTokens({ token, refreshToken: refreshTokenValue })
  }, [token, refreshTokenValue])

  // §10.8 — install the global 401 interceptor once.
  useEffect(() => {
    activateSessionGuard()
  }, [])

  // §10.8 — when an authenticated API request is rejected (401), the session is
  // dead server-side (e.g. the account was suspended/frozen). Clear it so the
  // app returns to login instead of rendering empty screens. No server logout
  // call — the token is already invalid. Depends on [token] so the guard only
  // acts while we believe we're signed in (a 401 when logged out is normal).
  useEffect(() => {
    const handleUnauthorized = () => {
      if (!token) return
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current)
        refreshTimerRef.current = null
      }
      disconnectConsumer()
      setUser(null)
      setToken(null)
      setRefreshTokenValue(null)
      setPermissions(null)
      void clearTokens()
    }
    window.addEventListener(UNAUTHORIZED_EVENT, handleUnauthorized)
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, handleUnauthorized)
  }, [token])

  // If we have a token but no user (e.g. page refresh with localStorage token), fetch user
  useEffect(() => {
    if (token && !user) {
      fetch(apiUrl('/api/v1/auth/me'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.success && data.data?.user) {
            setUser(mapUser(data.data.user))
          } else {
            setToken(null)
          }
        })
        .catch(() => {
          setToken(null)
        })
    }
  }, [token, user])

  // Fetch permissions when we have a token
  useEffect(() => {
    if (!token) return

    fetch(apiUrl('/api/v1/permissions'), {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.data?.permissions) {
          setPermissions(data.data.permissions)
        }
      })
      .catch(() => {
        // Permissions fetch failed — will use defaults
      })
  }, [token])

  // Register the device for native push once we have an authenticated token.
  // Web build is a no-op. The cleanup function unbinds the Capacitor
  // listeners on logout / token rotation so we never double-listen.
  useEffect(() => {
    if (!token) return
    let teardown: (() => Promise<void>) | null = null
    void (async () => {
      const { registerForPush } = await import('@/platform/push')
      teardown = await registerForPush({
        authToken: token,
        appVersion: (import.meta as { env?: { VITE_RELEASE?: string } }).env?.VITE_RELEASE,
      })
    })()
    return () => {
      if (teardown) void teardown()
    }
  }, [token])

  // Proactive token refresh: schedule refresh 1 minute before JWT expiry
  useEffect(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current)
      refreshTimerRef.current = null
    }

    if (!token || !refreshTokenValue) return

    try {
      // Decode JWT payload (base64url) to read exp
      const payload = JSON.parse(atob(token.split('.')[1]))
      const expiresAt = payload.exp * 1000 // ms
      const refreshAt = expiresAt - 60_000  // 1 minute before expiry
      const delay = refreshAt - Date.now()

      if (delay <= 0) {
        // Token already expired or about to expire — refresh immediately
        refreshTokenApi(refreshTokenValue)
          .then((data) => {
            setToken(data.token)
            setRefreshTokenValue(data.refresh_token)
          })
          .catch(() => {
            // Refresh failed — force logout
            setUser(null)
            setToken(null)
            setRefreshTokenValue(null)
            setPermissions(null)
            void clearTokens()
          })
      } else {
        refreshTimerRef.current = setTimeout(() => {
          refreshTokenApi(refreshTokenValue)
            .then((data) => {
              setToken(data.token)
              setRefreshTokenValue(data.refresh_token)
            })
            .catch(() => {
              setUser(null)
              setToken(null)
              setRefreshTokenValue(null)
              setPermissions(null)
              localStorage.removeItem('jwt-token')
              localStorage.removeItem('jwt-refresh-token')
            })
        }, delay)
      }
    } catch {
      // Invalid token format — ignore
    }

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current)
        refreshTimerRef.current = null
      }
    }
  }, [token, refreshTokenValue])

  const login = useCallback(async (email: string, password: string) => {
    try {
      const res = await fetch(apiUrl('/api/v1/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user: { email, password } }),
      })
      const data = await res.json()

      if (data.success && data.data?.token && data.data?.user) {
        setToken(data.data.token)
        if (data.data.refresh_token) setRefreshTokenValue(data.data.refresh_token)
        setUser(mapUser(data.data.user))
        return { success: true }
      }

      return { success: false, error: data.error || 'Invalid email or password' }
    } catch {
      return { success: false, error: 'Network error. Please try again.' }
    }
  }, [])

  const adoptSession = useCallback((payload: { token: string; refresh_token?: string; user: Record<string, unknown> }) => {
    setToken(payload.token)
    if (payload.refresh_token) setRefreshTokenValue(payload.refresh_token)
    setUser(mapUser(payload.user))
  }, [])

  const forgotPassword = useCallback(async (email: string) => {
    try {
      const res = await fetch(apiUrl('/api/v1/auth/forgot_password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      const data = await res.json()
      if (data.success) return { success: true }
      return { success: false, error: data.error || 'Request failed' }
    } catch {
      return { success: false, error: 'Network error. Please try again.' }
    }
  }, [])

  const resetPassword = useCallback(async (resetToken: string, password: string, passwordConfirmation: string) => {
    try {
      const res = await fetch(apiUrl('/api/v1/auth/reset_password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, password, password_confirmation: passwordConfirmation }),
      })
      const data = await res.json()
      if (data.success) return { success: true }
      return { success: false, error: data.error || 'Reset failed' }
    } catch {
      return { success: false, error: 'Network error. Please try again.' }
    }
  }, [])

  const resendVerification = useCallback(async () => {
    if (!token) return { success: false, error: 'Not authenticated' }
    try {
      const res = await fetch(apiUrl('/api/v1/auth/resend_verification'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      const data = await res.json()
      if (data.success) return { success: true }
      return { success: false, error: data.error || 'Failed to resend' }
    } catch {
      return { success: false, error: 'Network error. Please try again.' }
    }
  }, [token])

  const updateOnboarding = useCallback(async (stepData: Record<string, unknown>) => {
    if (!token) return { success: false, error: 'Not authenticated' }
    try {
      const res = await fetch(apiUrl('/api/v1/auth/onboarding'), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(stepData),
      })
      const data = await res.json()
      if (data.success && data.data?.user) {
        setUser(mapUser(data.data.user))
        return { success: true, trustBonus: data.data.trustBonus }
      }
      return { success: false, error: data.errors?.length ? data.errors.join('. ') : data.error || 'Update failed' }
    } catch {
      return { success: false, error: 'Network error. Please try again.' }
    }
  }, [token])

  const updateProfile = useCallback(async (profileData: Record<string, unknown> | FormData) => {
    if (!token || !user) return { success: false, error: 'Not authenticated' }
    try {
      const isFormData = profileData instanceof FormData
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${token}`,
      }
      if (!isFormData) {
        headers['Content-Type'] = 'application/json'
      }
      const res = await fetch(apiUrl(`/api/v1/users/${user.id}`), {
        method: 'PATCH',
        headers,
        body: isFormData ? profileData : JSON.stringify({ user: profileData }),
      })
      const data = await res.json()
      if (data.success && data.data?.user) {
        setUser(mapUser(data.data.user))
        return { success: true }
      }
      return { success: false, error: data.errors?.length ? data.errors.join('. ') : data.error || 'Update failed' }
    } catch {
      return { success: false, error: 'Network error. Please try again.' }
    }
  }, [token, user])

  const completeOnboarding = useCallback(async () => {
    if (!token) return { success: false, error: 'Not authenticated' }
    try {
      const res = await fetch(apiUrl('/api/v1/auth/complete_onboarding'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      const data = await res.json()
      if (data.success && data.data?.user) {
        setUser(mapUser(data.data.user))
        return { success: true }
      }
      return { success: false, error: data.error || 'Failed' }
    } catch {
      return { success: false, error: 'Network error. Please try again.' }
    }
  }, [token])

  const socialLogin = useCallback(async (provider: string, idToken: string, userInfo?: { firstName?: string; lastName?: string }) => {
    try {
      const result = await socialLoginApi(provider, idToken, userInfo)
      if (result.success && result.data) {
        const d = result.data as { token: string; refresh_token: string; user: Record<string, unknown> }
        setToken(d.token)
        if (d.refresh_token) setRefreshTokenValue(d.refresh_token)
        setUser(mapUser(d.user))
        return { success: true }
      }
      return { success: false, error: result.error || 'Social login failed' }
    } catch {
      return { success: false, error: 'Network error. Please try again.' }
    }
  }, [])

  const refreshUser = useCallback(async () => {
    if (!token) return
    try {
      const res = await fetch(apiUrl('/api/v1/auth/me'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      const data = await res.json()
      if (data.success && data.data?.user) {
        setUser(mapUser(data.data.user))
      }
    } catch {
      // Silently fail
    }
  }, [token])

  const logout = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current)
      refreshTimerRef.current = null
    }
    // Send logout request to invalidate refresh token server-side
    if (token && refreshTokenValue) {
      fetch(apiUrl('/api/v1/auth/logout'), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshTokenValue }),
      }).catch(() => {
        // Best-effort server-side invalidation
      })
    }
    disconnectConsumer()
    setUser(null)
    setToken(null)
    setRefreshTokenValue(null)
    setPermissions(null)
    void clearTokens()
  }, [token, refreshTokenValue])

  const value: AuthContextValue = {
    user,
    token,
    permissions,
    isAuthenticated: !!user && !!token,
    isRestoring,
    login,
    adoptSession,
    forgotPassword,
    resetPassword,
    resendVerification,
    updateOnboarding,
    updateProfile,
    completeOnboarding,
    socialLogin,
    refreshUser,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  return useContext(AuthContext)
}
